import * as React from 'react';
import Lazy from 'lazy.js';
import { parse } from 'query-string';
import { Component } from 'react';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { SchoolEventForm } from './SchoolEventsForm/SchoolEventForm';
import { SchoolForm } from 'Src/models/form';
import { EVENT_ADD_TEAM } from '../../../../../consts/event';
import { getEventType } from 'Src/helpers/accessor/accessor';
import moment from 'moment';
import { SchoolEventAddExistingTeam } from './SchoolEventAddExistingTeam/SchoolEventAddExistingTeam';
import {
  getMaxTeams,
  getMinTeams,
  isInterSchoolsEvent,
  isNonTeamSportEvent,
  isTeamOrTwoOnTwoSportEvent
} from '../../../../../helpers/event/event';
import { activateEvent, createEvent } from 'Src/helpers/service/admin/event';
import { addSchoolEventTeam } from 'Src/helpers/service/admin/tournamentTeams';
import { SchoolEventAddNewTeam } from './SchoolEventAddNewTeam/SchoolEventAddNewTeam';
import { EVENT_TYPE_TYPE } from '../../../../../types/event';
import { GENDER_TYPE } from '../../../../../types/common';
import { getVenueTypeByPostcode } from '../../../../../helpers/venue/venue';
import { MODE_TYPE, SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from '../../../../../consts/common';
import propz from 'propz';
import { updateSchoolEventIndividualsBatch } from '../../../../../helpers/service/admin/schoolEventIndividuals';
import { SimpleModal } from '../../../../../components/SimpleModal/SimpleModal';
import { TEAM_TYPE } from '../../../../../consts/team';
import { SchoolEventSaveTeamsForm } from './SchoolEventSaveTeamsForm/SchoolEventSaveTeamsForm';
import { createSchoolTeam } from '../../../../../helpers/service/admin/schoolTeams';
import { addEventToSchoolLeague } from 'Src/helpers/service/admin/schoolLeagues';
import { getKitLabKits } from 'Src/helpers/service/admin/kitLab';
import { deleteEvent, getSchoolEventDetails, updateSchoolEventDetails } from 'Src/helpers/service/admin/schoolEvents';
import { pickKitForEvent } from 'Src/helpers/kitLab/kitLab';
import './SchoolEventManager.scss';

export interface SchoolEventFromForm {
  ages: number[];
  distance: number;
  endTime: Date;
  eventType: EVENT_TYPE_TYPE;
  gender: GENDER_TYPE;
  houses: any[];
  multiparty: boolean;
  isStudentCanSubmitResult: boolean;
  isStudentCanJoin: boolean;
  clubId: boolean;
  participants: any[];
  sport: any;
  sportId: string;
  startTime: Date;
  venue: any;
  shortDescription?: string;
  photos: { picUrl: string }[];
  leaguePositionsCount: number;
  replyToEmail?: string;
}

type Step = 'EVENT_SUMMARY' | 'ADD_EXISTING_TEAM' | 'ADD_NEW_TEAM';

const EVENT_WIZARD_STEP: {
  [key: string]: Step;
} = {
  EVENT_SUMMARY: 'EVENT_SUMMARY',
  ADD_EXISTING_TEAM: 'ADD_EXISTING_TEAM',
  ADD_NEW_TEAM: 'ADD_NEW_TEAM'
};

const EVENT_WIZARD_STEP_MAPPING: {
  [key: string]: string;
} = {
  EVENT_SUMMARY: 'New event',
  ADD_EXISTING_TEAM: 'Add existing team',
  ADD_NEW_TEAM: 'Add new team'
};

async function autoAssignKitsForNonTeamEvent(
  user: AppUser,
  eventId: string,
  currentEvent: SchoolEventFromForm
): Promise<void> {
  const allKits = await getKitLabKits(user);
  const kits = allKits || [];

  const sportId = currentEvent.sportId;
  const eventGender = currentEvent.gender as any;

  const kit = pickKitForEvent(kits, sportId, eventGender);

  if (!kit) {
    return;
  }

  await updateSchoolEventDetails(user, eventId, {
    kitLabKits: [
      {
        kitId: kit._id
      }
    ]
  });
}

function getExplicitTeamKit(team: any, allKits: any[]): any | null {
  if (!Array.isArray(allKits) || !allKits.length) return null;

  const kitId = propz.get(team as any, ['kitLabKitId'], null);
  if (!kitId) return null;

  const kitIdStr = kitId.toString();
  return allKits.find(k => k._id && k._id.toString() === kitIdStr) || null;
}

function sanitizeTeamForSave(team: any): any {
  const { __playersSnapshot, ...rest } = team || {};
  const players = Array.isArray(rest.players)
    ? rest.players.map(player => ({
        userId: player.userId,
        permissionId: player.permissionId
      }))
    : [];

  return {
    ...rest,
    players
  };
}

function isAdhocTeamWithPlayers(team: any): boolean {
  return team && team.teamType === TEAM_TYPE.ADHOC && Array.isArray(team.players) && team.players.length > 0;
}

function mergeKitMappings(existing: any[] = [], updates: any[] = []): any[] {
  const byKey = new Map<string, any>();

  existing.forEach(m => {
    const key = m.teamId ? m.teamId.toString() : 'event';
    byKey.set(key, m);
  });

  updates.forEach(m => {
    const key = m.teamId ? m.teamId.toString() : 'event';
    byKey.set(key, m);
  });

  return Array.from(byKey.values());
}

async function autoAssignKitsForTeamEvent(
  user: AppUser,
  eventId: string,
  currentEvent: SchoolEventFromForm,
  savedTeams: any[]
): Promise<void> {
  if (!Array.isArray(savedTeams) || !savedTeams.length) {
    return;
  }

  const allKits = await getKitLabKits(user);
  if (!Array.isArray(allKits) || !allKits.length) {
    return;
  }

  const sportId = currentEvent.sportId;
  const eventGender = currentEvent.gender as any;

  const mappings: any[] = [];

  for (const team of savedTeams) {
    const teamId = (team._id || team.id || '').toString();
    if (!teamId) continue;

    let kit: any = getExplicitTeamKit(team, allKits as any[]);

    if (!kit) {
      kit = pickKitForEvent(allKits as any[], sportId, eventGender) as any;
    }

    if (!kit) {
      continue;
    }

    mappings.push({
      teamId,
      kitId: kit._id
    });
  }

  if (!mappings.length) {
    return;
  }

  const before = await getSchoolEventDetails(user, eventId).catch(e => {
    return null;
  });

  const existing: any[] = (before && (propz.get(before, ['kitLabKits'], []) as any[])) || [];
  const merged = mergeKitMappings(existing, mappings);

  await updateSchoolEventDetails(user, eventId, {
    kitLabKits: merged
  });
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  currentEvent?: SchoolEventFromForm;
  eventDraft?: any;
  currentStep: Step;
  forms: SchoolForm[];

  isLoading: boolean;

  addTeamOption: string;

  blackListTeamIds: string[];
  teams: any[];
  isSaveTeamsModalOpen: boolean;
  createdEventId?: string;
  pendingConflictingTeam?: any;
  isErrorModalOpen: boolean;
  errorMessage: string;
  teamPlayerConflicts: Array<{
    userId: string;
    playerName: string;
    currentTeamName: string;
    nextTeamName: string;
    choice: 'current' | 'next';
  }>;
}

export class SchoolEventManager extends Component<Props, State> {
  private isEventSaveInProgress = false;
  private topbarSyncTimer?: number;
  private pageRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);

    this.state = {
      currentEvent: undefined,
      eventDraft: undefined,
      forms: [],

      currentStep: EVENT_WIZARD_STEP.EVENT_SUMMARY,
      isLoading: false,
      addTeamOption: '',
      blackListTeamIds: [],
      teams: [],
      isSaveTeamsModalOpen: false,
      createdEventId: undefined,
      pendingConflictingTeam: undefined,
      isErrorModalOpen: false,
      errorMessage: '',
      teamPlayerConflicts: []
    };
  }

  componentDidMount() {
    this.topbarSyncTimer = window.setTimeout(this.syncTopbarWizard, 0);
    this.setState({
      isLoading: true
    });
    this.setItems();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.currentStep !== this.state.currentStep || prevState.createdEventId !== this.state.createdEventId) {
      this.syncTopbarWizard();
    }
  }

  componentWillUnmount() {
    if (this.topbarSyncTimer) window.clearTimeout(this.topbarSyncTimer);
    window.dispatchEvent(new CustomEvent('sit:topbar-wizard-clear'));
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  syncTopbarWizard = () => {
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: 'New event' }));

    if (this.state.createdEventId) {
      window.dispatchEvent(new CustomEvent('sit:topbar-wizard-clear'));
      return;
    }

    const isParticipantsStep =
      this.state.currentStep === EVENT_WIZARD_STEP.ADD_NEW_TEAM ||
      this.state.currentStep === EVENT_WIZARD_STEP.ADD_EXISTING_TEAM;

    window.dispatchEvent(
      new CustomEvent('sit:topbar-wizard', {
        detail: {
          steps: [
            { label: 'Event details', state: isParticipantsStep ? 'done' : 'active' },
            { label: 'Participants', state: isParticipantsStep ? 'active' : 'pending' }
          ]
        }
      })
    );
  };

  getCreateDate(): any {
    const { history } = this.props;
    const search = parse(history.location.search);
    const date = propz.get(search, ['date']);
    return date;
  }

  setItems() {
    const { user } = this.props;
    return getAllForms(user)
      .then(forms => {
        this.setState({
          forms: forms,
          isLoading: false
        });

        return true;
      })
      .catch(error => {
        this.showRequestError(error, 'The event form could not be loaded. Please try again.');
        return false;
      });
  }

  showRequestError = (error: any, fallbackMessage: string) => {
    const errorMessage =
      propz.get(error, ['response', 'data', 'details', 'text'], '') ||
      propz.get(error, ['response', 'data', 'message'], '') ||
      propz.get(error, ['message'], '') ||
      fallbackMessage;
    this.setState({ isLoading: false, isErrorModalOpen: true, errorMessage });
  };

  closeErrorModal = () => this.setState({ isErrorModalOpen: false, errorMessage: '' });

  getSteps(): Step[] {
    return [EVENT_WIZARD_STEP.EVENT_SUMMARY, EVENT_WIZARD_STEP.ADD_EXISTING_TEAM, EVENT_WIZARD_STEP.ADD_NEW_TEAM];
  }

  getStepTitle(): string {
    const { currentStep } = this.state;
    return EVENT_WIZARD_STEP_MAPPING[currentStep];
  }

  onCancelClick = () => {
    const { history } = this.props;

    history.push({
      pathname: '/events'
    });
  };

  onEventFormSubmit = data => {
    const { addTeamOption, ...rest } = data;

    const showParticipantsStepFromTop = () => {
      requestAnimationFrame(() => {
        const scrollContainer = this.pageRef.current?.closest('.bGenericContentColumn');
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
        } else {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
      });
    };

    switch (addTeamOption) {
      case EVENT_ADD_TEAM.NEW:
        this.setState(
          {
            currentEvent: rest,
            eventDraft: rest,
            addTeamOption: addTeamOption,
            currentStep: EVENT_WIZARD_STEP.ADD_NEW_TEAM
          },
          showParticipantsStepFromTop
        );
        break;
      case EVENT_ADD_TEAM.EXISTING:
        this.setState(
          {
            currentEvent: rest,
            eventDraft: rest,
            addTeamOption: addTeamOption,
            currentStep: EVENT_WIZARD_STEP.ADD_EXISTING_TEAM
          },
          showParticipantsStepFromTop
        );
        break;
      default:
        console.error('Add team option incorrect');
    }
  };

  showCreatedEvent = (eventId: string) => {
    this.setState({
      isLoading: false,
      isSaveTeamsModalOpen: false,
      createdEventId: eventId
    });
  };

  onCreateAnotherEventClick = () => {
    this.setState({
      currentEvent: undefined,
      eventDraft: undefined,
      currentStep: EVENT_WIZARD_STEP.EVENT_SUMMARY,
      addTeamOption: '',
      blackListTeamIds: [],
      teams: [],
      createdEventId: undefined
    });
  };

  onEventDraftChange = (eventDraft: any) => {
    this.setState({ eventDraft });
  };

  getDraftSelectionLabel(items: any, emptyLabel: string): string {
    if (!Array.isArray(items)) return emptyLabel;
    const selected = items.filter(item => item && item.isChecked);
    if (!selected.length) return emptyLabel;
    if (selected.length === 1) return String(selected[0].name || selected[0].label || selected[0].value || emptyLabel);
    return `${selected.length} selected`;
  }

  renderCreateHeader(): React.ReactNode {
    const draft: any = this.state.currentEvent || this.state.eventDraft || {};
    const sportName = String(propz.get(draft, ['sport', 'name'], '') || '').trim();
    const description = String(propz.get(draft, ['shortDescription'], '') || '').trim();
    const venueName = String(
      propz.get(
        draft,
        ['venue', 'name'],
        propz.get(draft, ['venue', 'placeData', 'name'], propz.get(draft, ['venue', 'postcode'], ''))
      ) || ''
    ).trim();
    const startTime = propz.get<any>(draft, ['startTime'], undefined);
    const endTime = propz.get<any>(draft, ['endTime'], undefined);
    const hasDate = startTime && moment(startTime).isValid();
    const eventType = propz.get(draft, ['eventType'], '');
    const eventTypeLabel = eventType ? getEventType(draft as SchoolEventFromForm) : '';
    const rawGender = propz.get(draft, ['gender'], '');
    const genderLabel = Array.isArray(rawGender)
      ? this.getDraftSelectionLabel(rawGender, '')
      : String(SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[rawGender] || rawGender || '');
    const ageLabel = Array.isArray(propz.get(draft, ['ages'], undefined))
      ? this.getDraftSelectionLabel(propz.get(draft, ['ages'], []), '')
      : '';
    const headline = description || (sportName ? `${sportName} event` : 'Create event');
    const isParticipantsStep = this.state.currentStep !== EVENT_WIZARD_STEP.EVENT_SUMMARY;

    return (
      <header className="bEventCreateHeader">
        <button type="button" className="bEventCreateHeader__back" onClick={this.onCancelClick}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Events
        </button>
        <i aria-hidden="true" />
        <div className="bEventCreateHeader__main">
          <span className="bEventCreateHeader__eyebrow">Sport &amp; Fixtures / New event</span>
          <h1>{headline}</h1>
          <div className="bEventCreateHeader__summary" aria-label="Event details entered so far">
            {sportName ? <span className="is-sport">{sportName}</span> : null}
            {hasDate ? (
              <span className="is-date">
                {moment(startTime).format('ddd D MMM')}
                {endTime && moment(endTime).isValid()
                  ? ` · ${moment(startTime).format('HH:mm')}–${moment(endTime).format('HH:mm')}`
                  : ''}
              </span>
            ) : null}
            {eventTypeLabel ? <span className="is-type">{eventTypeLabel}</span> : null}
            {genderLabel ? <span className="is-gender">{genderLabel}</span> : null}
            {ageLabel ? <span className="is-age">Ages: {ageLabel}</span> : null}
            {venueName ? <span className="is-venue">{venueName}</span> : null}
            {isParticipantsStep ? (
              <span className="is-teams">
                {this.state.teams.length} team{this.state.teams.length === 1 ? '' : 's'} added
              </span>
            ) : null}
          </div>
        </div>
        <span className="bEventCreateHeader__step">Step {isParticipantsStep ? '2' : '1'} of 2</span>
      </header>
    );
  }

  onViewCreatedEventClick = () => {
    const { createdEventId } = this.state;
    if (!createdEventId) return;

    this.props.history.push({
      pathname: '/events/event',
      search: `id=${createdEventId}`
    });
  };

  getPlayerDisplayName = (team: any, userId: string): string => {
    const snapshot = (propz.get<any[]>(team, ['__playersSnapshot'], []) || []).find(
      player => String(player.id || player.userId) === String(userId)
    );
    return snapshot ? `${snapshot.firstName || ''} ${snapshot.lastName || ''}`.trim() || 'Student' : 'Student';
  };

  addExistingTeamToState = (team: any, teams = this.state.teams) => {
    const { id } = team;
    const { blackListTeamIds } = this.state;
    const blackListTeamIdsUpdated = [...blackListTeamIds, id];
    const teamsUpdated = team ? [...teams, team] : [...teams];

    this.setState({
      blackListTeamIds: blackListTeamIdsUpdated,
      teams: teamsUpdated,
      currentStep: EVENT_WIZARD_STEP.ADD_EXISTING_TEAM,
      pendingConflictingTeam: undefined,
      teamPlayerConflicts: []
    });
  };

  onAddExistingTeamClick = team => {
    const { teams } = this.state;
    const conflicts = (team.players || [])
      .map(player => {
        const userId = String(player.userId);
        const currentTeam = teams.find(existingTeam =>
          (existingTeam.players || []).some(existingPlayer => String(existingPlayer.userId) === userId)
        );
        if (!currentTeam) return null;
        return {
          userId,
          playerName:
            this.getPlayerDisplayName(team, userId) !== 'Student'
              ? this.getPlayerDisplayName(team, userId)
              : this.getPlayerDisplayName(currentTeam, userId),
          currentTeamName: currentTeam.name || 'Current team',
          nextTeamName: team.name || 'New team',
          choice: 'current' as 'current'
        };
      })
      .filter(Boolean);

    if (!conflicts.length) {
      this.addExistingTeamToState(team);
      return;
    }

    this.setState({
      pendingConflictingTeam: team,
      teamPlayerConflicts: conflicts as State['teamPlayerConflicts']
    });
  };

  setTeamPlayerConflictChoice = (userId: string, choice: 'current' | 'next') => {
    this.setState(prevState => ({
      teamPlayerConflicts: prevState.teamPlayerConflicts.map(conflict =>
        conflict.userId === userId ? { ...conflict, choice } : conflict
      )
    }));
  };

  confirmTeamPlayerConflicts = () => {
    const { pendingConflictingTeam, teamPlayerConflicts, teams } = this.state;
    if (!pendingConflictingTeam) return;

    const keepInCurrent = new Set(
      teamPlayerConflicts.filter(conflict => conflict.choice === 'current').map(conflict => conflict.userId)
    );
    const moveToNext = new Set(
      teamPlayerConflicts.filter(conflict => conflict.choice === 'next').map(conflict => conflict.userId)
    );
    const nextTeam = {
      ...pendingConflictingTeam,
      players: (pendingConflictingTeam.players || []).filter(player => !keepInCurrent.has(String(player.userId))),
      __playersSnapshot: (pendingConflictingTeam.__playersSnapshot || []).filter(
        player => !keepInCurrent.has(String(player.id || player.userId))
      )
    };
    const resolvedTeams = teams.map(team => ({
      ...team,
      players: (team.players || []).filter(player => !moveToNext.has(String(player.userId))),
      __playersSnapshot: (team.__playersSnapshot || []).filter(
        player => !moveToNext.has(String(player.id || player.userId))
      )
    }));

    this.addExistingTeamToState(nextTeam, resolvedTeams);
  };

  closeTeamPlayerConflicts = () => this.setState({ pendingConflictingTeam: undefined, teamPlayerConflicts: [] });

  onAddNewTeamClick = team => {
    const { id } = team;
    const { blackListTeamIds, teams } = this.state;
    const blackListTeamIdsUpdated = id ? [...blackListTeamIds, id] : blackListTeamIds;
    const teamsUpdated = team ? [...teams, team] : [...teams];

    this.setState({
      blackListTeamIds: blackListTeamIdsUpdated,
      teams: teamsUpdated,
      currentStep: EVENT_WIZARD_STEP.ADD_NEW_TEAM
    });
  };

  onUpdateTeamClick = (teamIndex: number, team: any) => {
    this.setState(prevState => {
      const previousTeam = prevState.teams[teamIndex];
      const previousTeamId = previousTeam && previousTeam.id;
      const nextTeamId = team && team.id;
      const teamsUpdated = prevState.teams.map((existingTeam, index) => (index === teamIndex ? team : existingTeam));
      let blackListTeamIdsUpdated = prevState.blackListTeamIds;

      if (previousTeamId && previousTeamId !== nextTeamId) {
        blackListTeamIdsUpdated = blackListTeamIdsUpdated.filter(id => id !== previousTeamId);
      }

      if (nextTeamId && !blackListTeamIdsUpdated.includes(nextTeamId)) {
        blackListTeamIdsUpdated = [...blackListTeamIdsUpdated, nextTeamId];
      }

      return {
        blackListTeamIds: blackListTeamIdsUpdated,
        teams: teamsUpdated,
        currentStep: EVENT_WIZARD_STEP.ADD_NEW_TEAM
      };
    });
  };

  //copy-paste
  onModifiedDataEvent(currentEvent: SchoolEventFromForm, user: AppUser) {
    const {
      gender,
      ages,
      eventType,
      sportId,
      startTime,
      endTime,
      venue,
      participants,
      houses,
      multiparty,
      shortDescription,
      photos,
      isStudentCanSubmitResult,
      isStudentCanJoin,
      leaguePositionsCount,
      replyToEmail
    } = currentEvent;
    const { activeSchool } = user;
    const validPhotos = Array.isArray(photos)
      ? photos
          .filter(photo => photo && typeof photo.picUrl === 'string' && photo.picUrl.trim().length > 0)
          .map(photo => ({ ...photo, picUrl: photo.picUrl.trim() }))
      : [];

    //Important! Modify seconds and milliseconds in start time for navigation
    const now = new Date();
    const nowS = now.getSeconds();
    const nowMs = now.getMilliseconds();
    startTime.setSeconds(nowS, nowMs);

    const body = {
      gender: gender,
      shortDescription: shortDescription,
      multiparty: multiparty,
      eventType: eventType,
      sportId: sportId,
      venue: venue,
      ages: ages.map(age => Number(age)),
      houses: houses.map(house => house.id),
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      invitedSchoolIds: isInterSchoolsEvent(currentEvent)
        ? participants.map(participant => participant.id)
        : [user.activeSchoolId],
      photos: validPhotos,
      isStudentCanSubmitResult: isStudentCanSubmitResult,
      isStudentCanJoin: isStudentCanJoin,
      leaguePositionsCount: Number(leaguePositionsCount),
      replyToEmail: replyToEmail
    };

    switch (true) {
      case typeof venue !== 'undefined' && typeof venue.point !== 'undefined' && venue.point.coordinates.length > 0:
        if (venue.id === 'TBD') {
          delete body.venue.postcodeId;
          body.venue.venueType = 'TBD';
          break;
        }
        if (typeof venue.postcodeId === 'undefined') {
          body.venue.postcodeId = venue.id;
        }
        body.venue.venueType = getVenueTypeByPostcode(venue, activeSchool);
        break;

      case typeof venue !== 'undefined' && typeof venue.point === 'undefined':
        if (venue.id === 'TBD') {
          delete body.venue.postcodeId;
          body.venue.venueType = 'TBD';
          break;
        }
        body.venue.venueType = getVenueTypeByPostcode(venue, activeSchool);
        break;

      default:
        delete body.venue;
    }

    if (body.venue) {
      delete body.venue.id;
      delete body.venue.isHome;
      delete body.venue.name;
      delete body.venue.postcode;
    }
    return body;
  }

  onRemoveTeamClick = (teamIndex: number) => {
    this.setState(prevState => {
      const team = prevState.teams[teamIndex];
      const teamId = team && team.id;
      return {
        teams: prevState.teams.filter((_, index) => index !== teamIndex),
        blackListTeamIds: teamId ? prevState.blackListTeamIds.filter(id => id !== teamId) : prevState.blackListTeamIds
      };
    });
  };

  onSaveClick = (team, isCreateAnotherEvent: boolean) => {
    const { teams, currentEvent } = this.state;
    const { user, history, location } = this.props;

    if (!currentEvent) {
      console.error('[SchoolEventManager] currentEvent is empty onSaveClick');
      return;
    }

    const teamsUpdated = team ? [...teams, team] : [...teams];

    const isSomeNonEmptyTeamAdhoc = teamsUpdated.some(isAdhocTeamWithPlayers);
    const isShowSaveTeamsModal = isTeamOrTwoOnTwoSportEvent(currentEvent) && isSomeNonEmptyTeamAdhoc;

    const aggregationType = propz.get(location, ['state', 'aggregationType']);
    const leagueId = propz.get(location, ['state', 'leagueId']);
    const isAggregationTypeExist = typeof aggregationType !== 'undefined' && aggregationType !== '';

    if (isShowSaveTeamsModal) {
      this.setState({
        isSaveTeamsModalOpen: true,
        teams: teamsUpdated
      });
    } else {
      if (this.isEventSaveInProgress) return;
      this.isEventSaveInProgress = true;
      this.setState({
        isLoading: true,
        teams: teamsUpdated
      });

      const body = this.onModifiedDataEvent(currentEvent, user);

      let eventId;
      let isEventActivated = false;

      if (isNonTeamSportEvent(currentEvent)) {
        createEvent(user, body)
          .then(event => {
            eventId = event.id;
            const players = Lazy(teamsUpdated)
              .map(team => team.players)
              .flatten()
              .toArray();
            return players.length
              ? updateSchoolEventIndividualsBatch(user, eventId, {
                  add: players.map(item => ({ userId: item.userId, permissionId: item.permissionId }))
                })
              : Promise.resolve();
          })
          .then(() => {
            return autoAssignKitsForNonTeamEvent(user, eventId, currentEvent).catch(err => {
              console.error('[SchoolEventManager] kit assign failed, continuing', err);
            });
          })
          .then(() => {
            return activateEvent(user, eventId);
          })
          .then(event => {
            isEventActivated = true;
            return this.attachCreatedEventToLeague(event, leagueId, isAggregationTypeExist);
          })
          .then(event => {
            this.isEventSaveInProgress = false;
            if (isCreateAnotherEvent) {
              this.setState({
                isLoading: false,
                currentStep: EVENT_WIZARD_STEP.EVENT_SUMMARY,
                currentEvent: undefined
              });
            } else {
              this.showCreatedEvent(eventId);
            }
            return event;
          })
          .catch(async err => {
            console.error('[SchoolEventManager] event creation failed', err);
            if (eventId && !isEventActivated) {
              await deleteEvent(user, eventId).catch(cleanupError => {
                console.error('[SchoolEventManager] failed to remove incomplete draft event', cleanupError);
              });
            }
            this.isEventSaveInProgress = false;
            this.showRequestError(err, 'The event could not be created. Please try again.');
          });
      } else {
        createEvent(user, body)
          .then(event => {
            eventId = event.id;
            return Promise.all(
              teamsUpdated.map(team => {
                return addSchoolEventTeam(user, eventId, sanitizeTeamForSave(team));
              })
            );
          })
          .then(savedTeams => {
            return autoAssignKitsForTeamEvent(user, eventId, currentEvent, savedTeams).catch(err => {
              console.error('[SchoolEventManager] kit assign failed, continuing', err);
            });
          })
          .then(() => {
            return activateEvent(user, eventId);
          })
          .then(event => {
            isEventActivated = true;
            return this.attachCreatedEventToLeague(event, leagueId, isAggregationTypeExist);
          })
          .then(event => {
            this.isEventSaveInProgress = false;
            if (isCreateAnotherEvent) {
              this.setState({
                isLoading: false,
                currentStep: EVENT_WIZARD_STEP.EVENT_SUMMARY,
                currentEvent: undefined
              });
            } else {
              this.showCreatedEvent(eventId);
            }
            return event;
          })
          .catch(async err => {
            console.error('[SchoolEventManager] event creation failed', err);
            if (eventId && !isEventActivated) {
              await deleteEvent(user, eventId).catch(cleanupError => {
                console.error('[SchoolEventManager] failed to remove incomplete draft event', cleanupError);
              });
            }
            this.isEventSaveInProgress = false;
            this.showRequestError(err, 'The event could not be created. Please try again.');
          });
      }
    }
  };
  //return to initial state
  goBack = () => {
    this.setState({
      currentStep: EVENT_WIZARD_STEP.EVENT_SUMMARY,
      addTeamOption: '',
      blackListTeamIds: [],
      teams: []
    });
  };

  renderParticipantsModeSwitch(): React.ReactNode {
    const { currentStep, currentEvent } = this.state;

    if (!currentEvent || isNonTeamSportEvent(currentEvent)) {
      return null;
    }

    const options = [
      {
        step: EVENT_WIZARD_STEP.ADD_NEW_TEAM,
        label: 'Build a new team'
      },
      {
        step: EVENT_WIZARD_STEP.ADD_EXISTING_TEAM,
        label: 'Pick an existing team'
      }
    ];

    return (
      <div
        className="bEventParticipantsMode"
        role="tablist"
        aria-label="Participants mode"
        data-help-anchor="event-participants-mode"
      >
        {options.map(option => {
          const isActive = currentStep === option.step;

          return (
            <button
              key={option.step}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`bEventParticipantsMode__btn ${isActive ? 'bEventParticipantsMode__btn--active' : ''}`}
              onClick={() => {
                if (!isActive) {
                  this.setState({ currentStep: option.step });
                }
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }
  renderViewByStep(): React.ReactNode {
    const { currentStep, currentEvent, blackListTeamIds, teams, forms } = this.state;
    const { user, location } = this.props;

    const aggregationType = propz.get(location, ['state', 'aggregationType'], '');

    const minTeams = currentEvent ? getMinTeams(currentEvent) : 1;
    const maxTeams = currentEvent ? getMaxTeams(currentEvent) : 999999;

    const teamsCount = teams.length;
    const isSaveButtonEnabled = teamsCount <= maxTeams && teamsCount >= minTeams;
    const isAddTeamButtonEnabled = teamsCount < maxTeams;
    const createDate = this.getCreateDate();

    switch (currentStep) {
      case EVENT_WIZARD_STEP.EVENT_SUMMARY:
        return (
          <SchoolEventForm
            schoolEvent={currentEvent}
            onSubmit={this.onEventFormSubmit}
            onCancel={this.onCancelClick}
            user={user}
            forms={forms}
            createDate={createDate}
            aggregationType={aggregationType}
            onValuesChange={this.onEventDraftChange}
          />
        );

      case EVENT_WIZARD_STEP.ADD_NEW_TEAM:
        if (!currentEvent) return null;
        return (
          <SchoolEventAddNewTeam
            user={user}
            currentEvent={currentEvent}
            isSaveButtonDisabled={!isSaveButtonEnabled}
            isAddTeamButtonDisabled={!isAddTeamButtonEnabled}
            minTeams={minTeams}
            maxTeams={maxTeams}
            onAddExistingTeamClick={this.onAddExistingTeamClick}
            onAddNewTeamClick={this.onAddNewTeamClick}
            onUpdateTeamClick={this.onUpdateTeamClick}
            onSaveClick={this.onSaveClick}
            onRemoveTeamClick={this.onRemoveTeamClick}
            teams={teams}
            goBack={this.goBack}
            participantsModeSwitch={this.renderParticipantsModeSwitch()}
          />
        );

      case EVENT_WIZARD_STEP.ADD_EXISTING_TEAM: {
        if (!currentEvent) return null;
        return (
          <SchoolEventAddExistingTeam
            onAddExistingTeamClick={this.onAddExistingTeamClick}
            onAddNewTeamClick={this.onAddNewTeamClick}
            onSaveClick={this.onSaveClick}
            user={user}
            currentEvent={currentEvent}
            blackListTeamIds={blackListTeamIds}
            isSaveButtonDisabled={!isSaveButtonEnabled}
            isAddTeamButtonDisabled={!isAddTeamButtonEnabled}
            minTeams={minTeams}
            maxTeams={maxTeams}
            teams={teams}
            goBack={this.goBack}
            onRemoveTeamClick={this.onRemoveTeamClick}
            participantsModeSwitch={this.renderParticipantsModeSwitch()}
          />
        );
      }
    }
  }
  renderSaveTeamsModal() {
    const { user } = this.props;
    const { isSaveTeamsModalOpen, teams } = this.state;
    const teamsAdhoc = teams.filter(isAdhocTeamWithPlayers);

    return (
      <SimpleModal
        isOpen={isSaveTeamsModalOpen}
        customClass="eSaveEventTeamsModal"
        dialogStyle={{ maxWidth: 520 }}
        showHeader={false}
        showFooter={false}
        onBackdropClick={this.closeSaveTeamsModal}
      >
        <SchoolEventSaveTeamsForm
          onSubmit={this.onSaveTeamSubmit}
          onCancel={this.closeSaveTeamsModal}
          user={user}
          teamsAdhoc={teamsAdhoc}
        />
      </SimpleModal>
    );
  }

  closeSaveTeamsModal = () => this.setState({ isSaveTeamsModalOpen: false });

  renderTeamPlayerConflictsModal() {
    const { pendingConflictingTeam, teamPlayerConflicts } = this.state;
    return (
      <SimpleModal
        isOpen={Boolean(pendingConflictingTeam)}
        title="Player already selected"
        onCloseClick={this.closeTeamPlayerConflicts}
        onBackdropClick={this.closeTeamPlayerConflicts}
        showFooter={false}
        isBodyBold={false}
        customClass="eventTeamConflictModal"
        dialogStyle={{ maxWidth: 680 }}
      >
        <div className="eventTeamConflictModal__body">
          <p className="eventTeamConflictModal__intro">
            Some players are already assigned to another team in this event. Choose which team each player will
            represent.
          </p>
          <div className="eventTeamConflictModal__list">
            {teamPlayerConflicts.map(conflict => (
              <div className="eventTeamConflictModal__row" key={conflict.userId}>
                <strong>{conflict.playerName}</strong>
                <div className="eventTeamConflictModal__choices">
                  <label>
                    <input
                      type="radio"
                      name={`player-team-${conflict.userId}`}
                      checked={conflict.choice === 'current'}
                      onChange={() => this.setTeamPlayerConflictChoice(conflict.userId, 'current')}
                    />
                    <span>
                      Keep in <b>{conflict.currentTeamName}</b>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name={`player-team-${conflict.userId}`}
                      checked={conflict.choice === 'next'}
                      onChange={() => this.setTeamPlayerConflictChoice(conflict.userId, 'next')}
                    />
                    <span>
                      Move to <b>{conflict.nextTeamName}</b>
                    </span>
                  </label>
                </div>
              </div>
            ))}
          </div>
          <div className="eventTeamConflictModal__actions">
            <button type="button" onClick={this.closeTeamPlayerConflicts}>
              Cancel
            </button>
            <button type="button" onClick={this.confirmTeamPlayerConflicts}>
              Apply choices
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  onSaveTeamSubmit = (data: any) => {
    const { teams, currentEvent } = this.state;
    const { user, history, location } = this.props;

    if (!currentEvent) {
      console.error('[SchoolEventManager] currentEvent is empty onSaveTeamSubmit');
      return;
    }
    if (this.isEventSaveInProgress) return;
    this.isEventSaveInProgress = true;

    // Empty ad-hoc teams belong to the event, but there is nothing useful to
    // save as a reusable school-team template. Keep them in the event payload
    // while only presenting populated ad-hoc teams in the save-team dialog.
    const teamsNotOfferedForSave = teams.filter(team => !isAdhocTeamWithPlayers(team));

    const aggregationType = propz.get(location, ['state', 'aggregationType']);
    const leagueId = propz.get(location, ['state', 'leagueId']);
    const isAggregationTypeExist = typeof aggregationType !== 'undefined' && aggregationType !== '';

    const teamsToSaveAsPrototype = data
      .filter(team => team.isSave)
      .map(team => {
        const { isSave, ...rest } = team;

        return sanitizeTeamForSave({ ...rest, teamType: TEAM_TYPE.PROTOTYPE });
      });

    //get teams adhoc again, because team name may change
    const teamsAdhoc = data.map(team => {
      const { isSave, ...rest } = team;
      return sanitizeTeamForSave({ ...rest });
    });

    const teamsToEvent = [...teamsNotOfferedForSave, ...teamsAdhoc];

    this.setState({
      isLoading: true,
      isSaveTeamsModalOpen: false
    });

    const body = this.onModifiedDataEvent(currentEvent, user);
    let eventId;
    let isEventActivated = false;

    createEvent(user, body)
      .then(event => {
        eventId = event.id;
        return Promise.all(teamsToEvent.map(team => addSchoolEventTeam(user, eventId, sanitizeTeamForSave(team))));
      })
      .then(savedTeams => {
        return autoAssignKitsForTeamEvent(user, eventId, currentEvent, savedTeams).catch(err => {
          console.error('[SchoolEventManager] kit assign failed, continuing', err);
        });
      })
      .then(() => {
        return activateEvent(user, eventId);
      })
      .then(event => {
        isEventActivated = true;
        return this.attachCreatedEventToLeague(event, leagueId, isAggregationTypeExist);
      })
      .then(() => {
        return Promise.all(
          teamsToSaveAsPrototype.map(teamToSaveAsPrototype => createSchoolTeam(user, teamToSaveAsPrototype))
        );
      })
      .then(res => {
        this.isEventSaveInProgress = false;
        this.showCreatedEvent(eventId);
      })
      .catch(async err => {
        console.error('[SchoolEventManager] event creation failed', err);
        if (eventId && !isEventActivated) {
          await deleteEvent(user, eventId).catch(cleanupError => {
            console.error('[SchoolEventManager] failed to remove incomplete draft event', cleanupError);
          });
        }
        this.isEventSaveInProgress = false;
        this.showRequestError(err, 'The event could not be created. Please try again.');
      });
  };

  attachCreatedEventToLeague = (event: any, leagueId: string, shouldAttach: boolean) => {
    if (!shouldAttach) return Promise.resolve(event);

    const { user } = this.props;
    const eventId = propz.get(event, ['eventDetails', 'eventId'], event && event.id);
    return addEventToSchoolLeague(user, leagueId, { ...event, eventId })
      .then(() => event)
      .catch(error => {
        const reason =
          propz.get(error, ['response', 'data', 'details', 'text'], '') ||
          propz.get(error, ['response', 'data', 'message'], '') ||
          propz.get(error, ['message'], '');
        this.showRequestError(
          new Error(`The event was created, but it could not be added to the league.${reason ? ` ${reason}` : ''}`),
          'The event was created, but it could not be added to the league. Open the event or the league and try attaching it again.'
        );
        return event;
      });
  };

  renderErrorModal() {
    const { isErrorModalOpen, errorMessage } = this.state;
    return (
      <SimpleModal
        isOpen={isErrorModalOpen}
        title="Something went wrong"
        onCloseClick={this.closeErrorModal}
        onBackdropClick={this.closeErrorModal}
        showFooter={false}
        isBodyBold={false}
      >
        <p>{errorMessage}</p>
      </SimpleModal>
    );
  }

  render() {
    const { isLoading, isSaveTeamsModalOpen, createdEventId, currentEvent, teams } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (createdEventId && currentEvent) {
      const sportName = propz.get(currentEvent, ['sport', 'name'], 'Event');
      const eventType = getEventType(currentEvent);
      const venueName = propz.get(
        currentEvent,
        ['venue', 'name'],
        propz.get(currentEvent, ['venue', 'postcode'], 'Venue TBC')
      );
      const playersCount = teams.reduce((total, team) => total + Number(propz.get(team, ['players', 'length'], 0)), 0);

      return (
        <div className="bEventCreated">
          <div className="bEventCreated__hero">
            <span className="bEventCreated__check" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            <h1 className="bEventCreated__title">Event created</h1>
            <p className="bEventCreated__subtitle">Invitations and notifications are on their way.</p>
          </div>
          <section className="bEventCreated__card">
            <div className="bEventCreated__cardHead">
              <span className="bEventCreated__cardIcon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4zM7 6H4v1a4 4 0 0 0 4 4M17 6h3v1a4 4 0 0 1-4 4" />
                </svg>
              </span>
              <div>
                <h2>{sportName}</h2>
                {currentEvent.shortDescription && <p>{currentEvent.shortDescription}</p>}
              </div>
            </div>
            <dl className="bEventCreated__details">
              <div>
                <dt>When</dt>
                <dd>
                  {moment(currentEvent.startTime).format('D MMM YYYY, h:mm A')} -{' '}
                  {moment(currentEvent.endTime).format('h:mm A')}
                </dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>{eventType}</dd>
              </div>
              <div>
                <dt>Venue</dt>
                <dd>{venueName}</dd>
              </div>
              <div>
                <dt>Participants</dt>
                <dd>
                  {playersCount} player{playersCount === 1 ? '' : 's'} across {teams.length} team
                  {teams.length === 1 ? '' : 's'}
                </dd>
              </div>
            </dl>
          </section>
          <div className="bEventCreated__actions">
            <button type="button" className="bEventCreated__primary" onClick={this.onCreateAnotherEventClick}>
              Create another
            </button>
            <button type="button" className="bEventCreated__ghost" onClick={this.onViewCreatedEventClick}>
              View event
            </button>
          </div>
        </div>
      );
    }

    const isParticipantsStep =
      this.state.currentStep === EVENT_WIZARD_STEP.ADD_NEW_TEAM ||
      this.state.currentStep === EVENT_WIZARD_STEP.ADD_EXISTING_TEAM;
    const classes = [
      'bEventCreate',
      isParticipantsStep ? 'bEventCreate--participants' : '',
      isSaveTeamsModalOpen ? 'modal-open' : ''
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={classes} ref={this.pageRef}>
        {this.renderCreateHeader()}
        {this.renderErrorModal()}
        {this.renderSaveTeamsModal()}
        {this.renderTeamPlayerConflictsModal()}
        {this.renderViewByStep()}
      </div>
    );
  }
}
